import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'lucide-react';
import { apiCall } from '../utils/auth';
import { API_ENDPOINTS } from '../config/clerkApi';
import { isValidYouTubeUrl } from '../utils/youtubeHelpers';
import './HomePage.css';

const HomePage = () => {
  const { isSignedIn, getToken } = useAuth();
  const navigate = useNavigate();
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Simple validation - just check if it's a valid YouTube URL
      if (!isValidYouTubeUrl(youtubeUrl)) {
        setMessage('❌ Invalid YouTube URL. Please provide a valid YouTube playlist or video URL.');
        return;
      }

      console.log('🚀 Creating course from URL:', youtubeUrl);

      // Always use create-course endpoint regardless of URL type
      const response = await apiCall(API_ENDPOINTS.CREATE_COURSE, {
        method: 'POST',
        body: JSON.stringify({ url: youtubeUrl })
      }, getToken);

      if (response.ok) {
        const data = await response.json();
        
        // Check if it's a warning (not a playlist)
        if (data.warning) {
          console.log('⚠️ Warning:', data.warning);
          setMessage(`⚠️ ${data.warning}`);
          return;
        }
        
        console.log('✅ Course created:', data);
        
        setMessage('✅ Course created successfully!');
        setYoutubeUrl('');
        
        // Redirect based on content type
        setTimeout(() => {
          if (data.type === 'single_video' && data.video) {
            // Redirect to single video page
            navigate(`/video/${data.video.uuid_video}`);
          } else if (data.type === 'playlist' && data.playlist) {
            // Redirect to playlist page
            navigate(`/${data.playlist.uuid_playlist}`);
          } else {
            // Fallback to my-courses if type is unclear
            navigate('/my-courses');
          }
        }, 2000);
      } else {
        const error = await response.json();
        setMessage(`❌ Error: ${error.error || 'Failed to create course'}`);
      }
    } catch (error) {
      console.error('Course creation error:', error);
      setMessage('❌ Course creation error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-page">
      <div className="home-content">
        <div className="welcome-section">
          <h1 className="animated-title"><span className="gradient-text">Education Make Simple</span></h1>
        </div>

        {isSignedIn && (
          <div className="create-course-section">
            <h2>Create New Course</h2>
            <form onSubmit={handleSubmit} className="playlist-form">
              <div className="form-group">
                <div className="input-with-icon">
                  <Link className="input-icon" size={20} />
                  <input
                    type="url"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/playlist?list=..."
                    className="playlist-input"
                    required
                  />
                </div>
              </div>
              <button 
                type="submit" 
                className="create-button"
                disabled={loading || !youtubeUrl.trim()}
              >
                {loading ? 'Creating...' : 'Create Course'}
              </button>
            </form>

            {message && (
              <div className={`message ${
                message.includes('✅') ? 'success' : 
                message.includes('⚠️') ? 'warning' : 
                'error'
              }`}>
                {message}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage; 